import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import sv from 'date-fns/locale/sv';
import { Step2Props } from './types';
import { CalendarIcon, EditIcon, WalletIcon, InfoIcon } from '../create-contract/icons';
import { Tooltip } from '../../../components/common/tooltip.component';

// Swedish locale for the calendar labels (date-fns ships with react-datepicker).
registerLocale('sv', sv);

// Portal the calendar popper to <body> so no ancestor with overflow:hidden
// (rounded cards) or transform (entrance animation) can clip it.
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    ReactDOM.createPortal(children, document.body);

// Bridge the parent's ISO-string contract (yyyy-mm-dd) ↔ react-datepicker's Date.
const parseIso = (s?: string): Date | null => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};
const toIso = (d: Date | null): string => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const Step2AssignmentCompensation: React.FC<Step2Props> = (props) => {
    const {
        t, errors, setErrors,
        // Assignment
        startDate, setStartDate,
        endDate, setEndDate,
        noEndDate, setNoEndDate,
        hasNoticePeriod, setHasNoticePeriod,
        noticePeriod, setNoticePeriod,
        assignmentDesc, setAssignmentDesc,
        // Compensation
        compType, setCompType,
        unit, setUnit,
        currency, setCurrency,
        amount, setAmount,
    } = props;

    const { i18n } = useTranslation();
    const calendarLocale = i18n.language?.toLowerCase().startsWith('sv') ? 'sv' : undefined;

    const clearError = (key: string) =>
        setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });

    return (
        <>
            {/* ── Offer Period ── */}
            <div className="cc-card">
                <div className="cc-card-header">
                    <span className="cc-card-icon"><CalendarIcon /></span>
                    <div className="cc-card-heading-text">
                        <h3 className="cc-card-title">{t('CO_ASSIGN_PERIOD_TITLE')}</h3>
                    </div>
                </div>
                <div className="cc-card-body">
                    <div className="cc-date-row">

                        <div className="cc-field" style={{ maxWidth: 360 }}>
                            <label className="cc-label">{t('CO_ASSIGN_START_DATE')}</label>
                            <div className="cc-dp-wrap">
                                <DatePicker
                                    selected={parseIso(startDate)}
                                    onChange={(d: Date | null) => { setStartDate(toIso(d)); clearError('startDate'); }}
                                    dateFormat="yyyy/MM/dd"
                                    placeholderText={(t('CO_DATE_PLACEHOLDER') || 'yyyy/mm/dd') as string}
                                    className={`cc-input${errors.startDate ? ' cc-input--error' : ''}`}
                                    wrapperClassName="cc-dp__input-dp"
                                    popperContainer={PopperPortal}
                                    popperProps={{ strategy: 'fixed' }}
                                    popperClassName="cc-dp__popper"
                                    calendarClassName="cc-dp__calendar"
                                    onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="scroll"
                                    locale={calendarLocale}
                                    autoComplete="off"
                                />
                                <span className="cc-dp-icon" aria-hidden="true"><CalendarIcon /></span>
                            </div>
                            {errors.startDate && <span className="cc-field-error">{errors.startDate}</span>}
                        </div>

                        <div className="cc-field" style={{ maxWidth: 360 }}>
                            <label className="cc-label">{t('CO_ASSIGN_END_DATE')}</label>
                            <div className={`cc-dp-wrap${noEndDate ? ' cc-dp-wrap--disabled' : ''}`}>
                                <DatePicker
                                    selected={parseIso(endDate)}
                                    onChange={(d: Date | null) => { setEndDate(toIso(d)); clearError('endDate'); }}
                                    dateFormat="yyyy/MM/dd"
                                    placeholderText={(t('CO_DATE_PLACEHOLDER') || 'yyyy/mm/dd') as string}
                                    className={`cc-input${errors.endDate ? ' cc-input--error' : ''}`}
                                    wrapperClassName="cc-dp__input-dp"
                                    popperContainer={PopperPortal}
                                    popperProps={{ strategy: 'fixed' }}
                                    popperClassName="cc-dp__popper"
                                    calendarClassName="cc-dp__calendar"
                                    onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="scroll"
                                    locale={calendarLocale}
                                    autoComplete="off"
                                    disabled={noEndDate}
                                />
                                <span className="cc-dp-icon" aria-hidden="true"><CalendarIcon /></span>
                            </div>
                            {errors.endDate && <span className="cc-field-error">{errors.endDate}</span>}
                        </div>

                        <span className="cc-checkbox-with-info">
                            <label className="cc-checkbox-wrap">
                                <input
                                    type="checkbox"
                                    className="cc-checkbox"
                                    checked={noEndDate}
                                    onChange={e => { setNoEndDate(e.target.checked); clearError('endDate'); }}
                                />
                                <span>{t('CO_ASSIGN_NO_END_DATE')}</span>
                            </label>
                            <Tooltip content={t('CO_ASSIGN_NO_END_DATE_INFO') as string}>
                                <span className="cc-info-icon" tabIndex={0} aria-label={t('CO_ASSIGN_NO_END_DATE_INFO') as string}>
                                    <InfoIcon />
                                </span>
                            </Tooltip>
                        </span>
{/* 
                        <span className="cc-checkbox-with-info">
                            <label className="cc-checkbox-wrap">
                                <input
                                    type="checkbox"
                                    className="cc-checkbox"
                                    checked={hasNoticePeriod}
                                    onChange={e => setHasNoticePeriod(e.target.checked)}
                                />
                                <span>{t('CO_ASSIGN_NOTICE_CHECKBOX')}</span>
                            </label>
                            <Tooltip content={t('CO_ASSIGN_NOTICE_INFO') as string}>
                                <span className="cc-info-icon" tabIndex={0} aria-label={t('CO_ASSIGN_NOTICE_INFO') as string}>
                                    <InfoIcon />
                                </span>
                            </Tooltip>
                        </span> */}
                    </div>

                    {/* {hasNoticePeriod && (
                        <div className="cc-field" style={{ maxWidth: 360, marginTop: 16 }}>
                            <label className="cc-label">{t('CO_ASSIGN_NOTICE_LABEL')}</label>
                            <select
                                className="cc-input cc-select"
                                value={noticePeriod}
                                onChange={e => setNoticePeriod(e.target.value)}
                            >
                                <option>{t('CO_ASSIGN_NOTICE_1M')}</option>
                                <option>{t('CO_ASSIGN_NOTICE_2M')}</option>
                                <option>{t('CO_ASSIGN_NOTICE_3M')}</option>
                                <option>{t('CO_ASSIGN_NOTICE_6M')}</option>
                            </select>
                            <p className="cc-hint-text">{t('CO_ASSIGN_NOTICE_HINT')}</p>
                        </div>
                    )} */}
                </div>
            </div>

            {/* ── Assignment Description ── */}
            <div className="cc-card cc-card--mt">
                <div className="cc-card-header">
                    <span className="cc-card-icon"><EditIcon /></span>
                    <div className="cc-card-heading-text">
                        <h3 className="cc-card-title">{t('CO_ASSIGN_DESC_TITLE')}</h3>
                    </div>
                </div>
                <div className="cc-card-body">
                    <div className="cc-field">
                        <label className="cc-label">{t('CO_ASSIGN_DESC_LABEL')}</label>
                        <textarea
                            className={`cc-textarea${errors.assignmentDesc ? ' cc-input--error' : ''}`}
                            value={assignmentDesc}
                            onChange={e => { setAssignmentDesc(e.target.value); clearError('assignmentDesc'); }}
                            placeholder={t('CO_ASSIGN_DESC_HINT')}
                            rows={5}
                        />
                        {errors.assignmentDesc && <span className="cc-field-error">{errors.assignmentDesc}</span>}
                    </div>
                </div>
            </div>

            {/* ── Compensation ── */}
            <div className="cc-card cc-card--mt">
                <div className="cc-card-header">
                    <span className="cc-card-icon"><WalletIcon /></span>
                    <div className="cc-card-heading-text">
                        <h3 className="cc-card-title">{t('CO_COMP_TITLE')}</h3>
                    </div>
                </div>
                <div className="cc-card-body">
                    <div className="cc-grid-3">

                        <div className="cc-field">
                            <label className="cc-label">{t('CO_COMP_TYPE_LABEL')}</label>
                            <select
                                className={`cc-input cc-select${errors.compType ? ' cc-input--error' : ''}`}
                                value={compType}
                                onChange={e => { setCompType(e.target.value); clearError('compType'); }}
                            >
                                <option value="">{t('CO_COMP_TYPE_PLACEHOLDER')}</option>
                                <option value="per_hour">{t('CO_COMP_TYPE_PER_HOUR')}</option>
                                <option value="per_day">{t('CO_COMP_TYPE_PER_DAY')}</option>
                                <option value="per_week">{t('CO_COMP_TYPE_PER_WEEK')}</option>
                                <option value="per_month">{t('CO_COMP_TYPE_PER_MONTH')}</option>
                                <option value="as_agreed">{t('CO_COMP_TYPE_AS_AGREED')}</option>
                                <option value="per_unit">{t('CO_COMP_TYPE_PER_UNIT')}</option>
                            </select>
                            {errors.compType && <span className="cc-field-error">{errors.compType}</span>}
                        </div>

                        {compType === 'per_unit' && (
                            <div className="cc-field">
                                <label className="cc-label">{t('CO_COMP_UNIT_LABEL')}</label>
                                <input
                                    className={`cc-input${errors.unit ? ' cc-input--error' : ''}`}
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={unit}
                                    onChange={e => {
                                        const val = e.target.value;
                                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                            setUnit(val);
                                            clearError('unit');
                                        }
                                    }}
                                    onKeyDown={e => {
                                        if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                                    }}
                                    placeholder={t('CO_COMP_UNIT_HINT')}
                                />
                                {errors.unit && <span className="cc-field-error">{errors.unit}</span>}
                            </div>
                        )}

                        <div className="cc-field">
                            <label className="cc-label">{t('CO_COMP_CURRENCY_LABEL')}</label>
                            <select
                                className="cc-input cc-select"
                                value={currency}
                                onChange={e => setCurrency(e.target.value)}
                            >
                                <option value="SEK">SEK</option>
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                                <option value="CHF">CHF</option>
                                <option value="CAD">CAD</option>
                                <option value="AUD">AUD</option>
                                <option value="NOK">NOK</option>
                                <option value="DKK">DKK</option>
                                <option value="JPY">JPY</option>
                                <option value="CNY">CNY</option>
                            </select>
                        </div>

                        <div className="cc-field">
                            <label className="cc-label">{t('CO_COMP_AMOUNT_LABEL')}</label>
                            <input
                                className={`cc-input${errors.amount ? ' cc-input--error' : ''}`}
                                type="number"
                                min="0"
                                step="any"
                                value={amount}
                                onChange={e => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                        setAmount(val);
                                        clearError('amount');
                                    }
                                }}
                                onKeyDown={e => {
                                    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                                }}
                                placeholder={t('CO_COMP_AMOUNT_HINT')}
                            />
                            {errors.amount && <span className="cc-field-error">{errors.amount}</span>}
                        </div>

                    </div>
                    <div className="cc-alert-box" style={{ marginTop: 16 }}>
                        <p>{t('CO_COMP_INVOICE_NOTE')}</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Step2AssignmentCompensation;
